import { useState, useEffect, useMemo } from 'react';
import { TvaToggle, useTva, tvaPct, tvaAmount } from './TvaToggle';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import QuickAddEntrepriseButton from './QuickAddEntrepriseButton';
import {
  CreditCard, Calendar, PenLine, BookOpen, Book, Mail, Shirt, FileText, Package,
  Plus, Trash2, Search, X, Check, Building2, Truck, ArrowLeft, Send, Download, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const fmtXAF = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

// Catalogue par défaut des supports de communication (comme sur la maquette)
// Organisé par catégorie : Supports à forte demande / Supports entreprises / Objets publicitaires
const DEFAULT_SUPPORTS = [
  // Legacy (compatibilité avec anciens devis)
  { key: 'carte_visite',      designation: 'Carte de visite',       category: 'entreprises',    icon: 'credit-card',  color: 'from-slate-100 to-blue-100',  prix: 15,     qte: 500 },
  { key: 'calendrier_mural',  designation: 'Calendrier mural',      category: 'objets',         icon: 'calendar',     color: 'from-blue-100 to-cyan-100',   prix: 850,    qte: 100 },
  { key: 'calendrier_chev',   designation: 'Calendrier chevalet',   category: 'objets',         icon: 'calendar',     color: 'from-cyan-100 to-teal-100',   prix: 700,    qte: 100 },
  { key: 'stylo',             designation: 'Stylo personnalisé',    category: 'objets',         icon: 'pen-line',     color: 'from-slate-100 to-slate-200', prix: 120,    qte: 500 },
  { key: 'notebook',          designation: 'Notebook',              category: 'entreprises',    icon: 'book-open',    color: 'from-indigo-100 to-blue-100', prix: 1500,   qte: 200 },
  { key: 'cahier',            designation: 'Cahier',                category: 'entreprises',    icon: 'book',         color: 'from-amber-100 to-orange-100', prix: 650,    qte: 300 },
  { key: 'enveloppe',         designation: 'Enveloppe',             category: 'entreprises',    icon: 'mail',         color: 'from-slate-100 to-slate-200', prix: 200,    qte: 500 },
  { key: 'pochette_rabat',    designation: 'Pochette à rabat',      category: 'entreprises',    icon: 'file-text',    color: 'from-slate-100 to-neutral-100', prix: 800,  qte: 300 },
  { key: 'tshirt',            designation: 'T-shirt personnalisé',  category: 'objets',         icon: 'shirt',        color: 'from-neutral-100 to-slate-200', prix: 2500, qte: 50 },
  // ── Supports à forte demande ─────────────────────────────
  { key: 'flyer',             designation: 'Flyer / prospectus',    category: 'forte_demande',  icon: 'file-text',    color: 'from-orange-100 to-amber-200', prix: 150,   qte: 500 },
  { key: 'depliant',          designation: 'Dépliant 2 ou 3 volets', category: 'forte_demande', icon: 'file-text',    color: 'from-amber-100 to-yellow-200', prix: 300,   qte: 500 },
  { key: 'brochure',          designation: 'Brochure',              category: 'forte_demande',  icon: 'book-open',    color: 'from-blue-100 to-indigo-200',  prix: 1200,  qte: 200 },
  { key: 'catalogue',         designation: 'Catalogue',             category: 'forte_demande',  icon: 'book',         color: 'from-indigo-100 to-violet-200', prix: 2500, qte: 100 },
  { key: 'affiche_a4a3',      designation: 'Affiche A4 / A3 / A2',  category: 'forte_demande',  icon: 'file-text',    color: 'from-rose-100 to-pink-200',    prix: 500,   qte: 100 },
  { key: 'kakemono',          designation: 'Kakémono / Roll-up',    category: 'forte_demande',  icon: 'package',      color: 'from-teal-100 to-cyan-200',    prix: 15000, qte: 2 },
  { key: 'totem',             designation: 'Totem publicitaire',    category: 'forte_demande',  icon: 'package',      color: 'from-cyan-100 to-blue-200',    prix: 25000, qte: 1 },
  { key: 'beach_flag',        designation: 'Drapeau publicitaire (Beach Flag)', category: 'forte_demande', icon: 'package', color: 'from-emerald-100 to-teal-200', prix: 20000, qte: 2 },
  { key: 'panneau_pub',       designation: 'Panneau publicitaire',  category: 'forte_demande',  icon: 'package',      color: 'from-lime-100 to-green-200',   prix: 50000, qte: 1 },
  { key: 'plaque_pro',        designation: 'Plaque professionnelle', category: 'forte_demande', icon: 'package',      color: 'from-slate-200 to-zinc-300',   prix: 8000,  qte: 1 },
  { key: 'sticker',           designation: 'Sticker / autocollant', category: 'forte_demande',  icon: 'file-text',    color: 'from-fuchsia-100 to-pink-200', prix: 100,   qte: 500 },
  { key: 'etiquette_prod',    designation: 'Étiquette produit',     category: 'forte_demande',  icon: 'credit-card',  color: 'from-pink-100 to-rose-200',    prix: 50,    qte: 1000 },
  { key: 'adhesif_vitrine',   designation: 'Adhésif vitrine',       category: 'forte_demande',  icon: 'file-text',    color: 'from-blue-100 to-sky-200',     prix: 3000,  qte: 5 },
  { key: 'adhesif_vehicule',  designation: 'Adhésif véhicule',      category: 'forte_demande',  icon: 'package',      color: 'from-sky-100 to-blue-200',     prix: 12000, qte: 1 },
  { key: 'bache_pub',         designation: 'Bâche publicitaire',    category: 'forte_demande',  icon: 'package',      color: 'from-violet-100 to-purple-200', prix: 5000, qte: 5 },
  { key: 'banderole',         designation: 'Banderole',             category: 'forte_demande',  icon: 'package',      color: 'from-purple-100 to-fuchsia-200', prix: 4500, qte: 5 },
  { key: 'oriflamme',         designation: 'Oriflamme',             category: 'forte_demande',  icon: 'package',      color: 'from-red-100 to-rose-200',     prix: 6000,  qte: 3 },
  // ── Supports pour les entreprises ───────────────────────
  { key: 'papier_entete',     designation: 'Papier à en-tête',      category: 'entreprises',    icon: 'file-text',    color: 'from-slate-100 to-slate-200',  prix: 100,   qte: 500 },
  { key: 'chemise_rabat',     designation: 'Chemise à rabat',       category: 'entreprises',    icon: 'file-text',    color: 'from-slate-100 to-neutral-200', prix: 400, qte: 200 },
  { key: 'bloc_notes',        designation: 'Bloc-notes',            category: 'entreprises',    icon: 'book',         color: 'from-yellow-100 to-amber-200', prix: 350,   qte: 200 },
  { key: 'carte_fidelite',    designation: 'Carte de fidélité',     category: 'entreprises',    icon: 'credit-card',  color: 'from-emerald-100 to-green-200', prix: 60,   qte: 500 },
  { key: 'invitation',        designation: 'Invitation',            category: 'entreprises',    icon: 'mail',         color: 'from-rose-100 to-pink-200',    prix: 200,   qte: 200 },
  { key: 'carte_voeux',       designation: 'Carte de vœux',         category: 'entreprises',    icon: 'mail',         color: 'from-red-100 to-rose-200',     prix: 250,   qte: 200 },
  { key: 'certificat',        designation: 'Certificat / diplôme',  category: 'entreprises',    icon: 'file-text',    color: 'from-yellow-100 to-orange-200', prix: 500,  qte: 100 },
  { key: 'menu_restaurant',   designation: 'Menu / carte de restaurant', category: 'entreprises', icon: 'book-open',  color: 'from-amber-100 to-orange-200', prix: 1500,  qte: 50 },
  { key: 'pochette_presentation', designation: 'Pochette de présentation', category: 'entreprises', icon: 'file-text', color: 'from-blue-100 to-indigo-200', prix: 900, qte: 200 },
  { key: 'dossier_commercial', designation: 'Dossier commercial',   category: 'entreprises',    icon: 'file-text',    color: 'from-indigo-100 to-blue-200',  prix: 1200,  qte: 100 },
  // ── Objets publicitaires ────────────────────────────────
  { key: 'mug',               designation: 'Mug personnalisé',      category: 'objets',         icon: 'package',      color: 'from-orange-100 to-red-200',   prix: 2500,  qte: 50 },
  { key: 'gourde',            designation: 'Gourde',                category: 'objets',         icon: 'package',      color: 'from-sky-100 to-blue-200',     prix: 3500,  qte: 50 },
  { key: 'tote_bag',          designation: 'Sac shopping / tote bag', category: 'objets',       icon: 'shirt',        color: 'from-emerald-100 to-teal-200', prix: 2000, qte: 100 },
  { key: 'casquette',         designation: 'Casquette',             category: 'objets',         icon: 'shirt',        color: 'from-blue-100 to-cyan-200',    prix: 3500,  qte: 50 },
  { key: 'polo',              designation: 'Polo',                  category: 'objets',         icon: 'shirt',        color: 'from-slate-100 to-blue-200',   prix: 5500,  qte: 30 },
  { key: 'gilet',             designation: 'Gilet',                 category: 'objets',         icon: 'shirt',        color: 'from-neutral-100 to-slate-200', prix: 4500, qte: 30 },
  { key: 'porte_cles',        designation: 'Porte-clés',            category: 'objets',         icon: 'package',      color: 'from-amber-100 to-yellow-200', prix: 800,   qte: 100 },
  { key: 'badge',             designation: 'Badge',                 category: 'objets',         icon: 'credit-card',  color: 'from-teal-100 to-cyan-200',    prix: 300,   qte: 200 },
  { key: 'bracelet',          designation: 'Bracelet',              category: 'objets',         icon: 'package',      color: 'from-fuchsia-100 to-pink-200', prix: 500,   qte: 200 },
  { key: 'parapluie',         designation: 'Parapluie',             category: 'objets',         icon: 'package',      color: 'from-indigo-100 to-blue-200',  prix: 6500,  qte: 20 },
  { key: 'calendrier',        designation: 'Calendrier',            category: 'objets',         icon: 'calendar',     color: 'from-yellow-100 to-amber-200', prix: 1200,  qte: 100 },
  { key: 'agenda',            designation: 'Agenda',                category: 'objets',         icon: 'book',         color: 'from-emerald-100 to-green-200', prix: 2500, qte: 100 },
];

const SUPPORTS_CATEGORIES = [
  { key: 'forte_demande', label: 'Supports à forte demande', color: 'text-orange-700', bg: 'bg-orange-50' },
  { key: 'entreprises',   label: 'Supports pour les entreprises', color: 'text-blue-700', bg: 'bg-blue-50' },
  { key: 'objets',        label: 'Objets publicitaires', color: 'text-emerald-700', bg: 'bg-emerald-50' },
];

const ICON_MAP = {
  'credit-card': CreditCard,
  'calendar': Calendar,
  'pen-line': PenLine,
  'book-open': BookOpen,
  'book': Book,
  'mail': Mail,
  'file-text': FileText,
  'shirt': Shirt,
  'package': Package,
};

const TRANSPORT_MODES = [
  'Transport routier',
  'Transport aérien',
  'Transport maritime',
  'Livraison locale',
  'Retrait sur place',
];

export default function SupportsImprimesDevisModal({ open, onOpenChange, onSaved }) {
  const [prospects, setProspects] = useState([]);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedProspect, setSelectedProspect] = useState(null);

  const [items, setItems] = useState(DEFAULT_SUPPORTS.map((s) => ({ ...s })));
  const [selectedKeys, setSelectedKeys] = useState(new Set()); // supports cochés à inclure dans le devis
  const [activeCategoryTab, setActiveCategoryTab] = useState('forte_demande');
  const [transport, setTransport] = useState({ mode: 'Transport routier', lieu: '', prix: 0 });
  const [notes, setNotes] = useState('');
  const [validiteDate, setValiditeDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedDevis, setSavedDevis] = useState(null);  // devis créé → afficher "Envoyer aux fournisseurs"
  const [showFournisseursModal, setShowFournisseursModal] = useState(false);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loadingFours, setLoadingFours] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset & load prospects
    setSearch('');
    setSelectedProspect(null);
    setItems(DEFAULT_SUPPORTS.map((s) => ({ ...s })));
    setTransport({ mode: 'Transport routier', lieu: '', prix: 0 });
    setNotes('');
    setSavedDevis(null);
    setShowFournisseursModal(false);
    // Date valide par défaut = +30 jours
    const d = new Date();
    d.setDate(d.getDate() + 30);
    setValiditeDate(d.toISOString().slice(0, 10));

    const load = async () => {
      setLoadingProspects(true);
      try {
        const r = await axios.get('/prospects');
        setProspects(r.data || []);
      } catch (e) {
        console.error(e);
        toast.error('Impossible de charger la liste des prospects');
      } finally {
        setLoadingProspects(false);
      }
    };
    load();
  }, [open]);

  const filteredProspects = useMemo(() => {
    if (!search) return prospects;
    const t = search.toLowerCase();
    return prospects.filter((p) =>
      (p.entreprise || '').toLowerCase().includes(t) ||
      (p.contact || '').toLowerCase().includes(t) ||
      (p.email || '').toLowerCase().includes(t) ||
      (p.telephone || '').toLowerCase().includes(t)
    );
  }, [prospects, search]);

  const updateItem = (key, field, value) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, [field]: value } : it)));
  };
  const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));
  const addItem = () => {
    const name = window.prompt('Nom du support à ajouter :');
    if (!name || !name.trim()) return;
    setItems((prev) => [
      ...prev,
      { key: `custom_${Date.now()}`, designation: name.trim(), icon: 'package', color: 'from-slate-100 to-slate-200', prix: 0, qte: 1 },
    ]);
  };

  // Filtrer sur la catégorie active + inclure les items déjà cochés + ceux ayant une quantité
  const visibleItems = useMemo(() => {
    return items.filter(it => {
      if (selectedKeys.has(it.key)) return true; // toujours afficher si coché
      return it.category === activeCategoryTab;
    });
  }, [items, activeCategoryTab, selectedKeys]);

  // Seuls les supports COCHÉS avec quantité > 0 sont inclus dans le devis
  const filledItems = items.filter((it) => selectedKeys.has(it.key) && (Number(it.qte) || 0) > 0);
  const sousTotal = filledItems.reduce((s, it) => s + (Number(it.prix) || 0) * (Number(it.qte) || 0), 0);
  const transportPrix = Number(transport.prix) || 0;
  const totalGeneral = sousTotal + transportPrix;
  const [tva, setTva] = useTva();
  const tvaMt = tvaAmount(totalGeneral, tva);

  const canSave = selectedProspect && filledItems.length > 0;

  const save = async () => {
    if (!selectedProspect) { toast.error('Sélectionnez d\'abord une entreprise'); return; }
    if (filledItems.length === 0) { toast.error('Ajoutez au moins un support avec quantité > 0'); return; }
    setSaving(true);
    try {
      const payload = {
        service_id: 'supports-imprimes',
        service_titre: 'Supports imprimés',
        client_nom: selectedProspect.entreprise || '',
        client_email: selectedProspect.email || '',
        client_telephone: selectedProspect.telephone || '',
        client_adresse: selectedProspect.adresse || '',
        client_prospect_id: selectedProspect.id || null,
        supports_produits: filledItems.map((it) => ({
          designation: it.designation,
          quantite: parseInt(it.qte, 10) || 0,
          prix_unitaire: parseFloat(it.prix) || 0,
        })),
        transport_mode: transport.mode,
        transport_lieu: transport.lieu,
        transport_prix: transportPrix,
        notes,
        validite_date: validiteDate,
        modele: 'Supports imprimés',
        prix_unitaire: 0,
        quantite: 0,
        remise_pct: 0,
        tva_pct: tvaPct(tva),
        prestations: [],
      };
      const r = await axios.post('/conception/devis', payload);
      toast.success(`Devis ${r.data.numero} généré avec succès`);
      setSavedDevis(r.data);
      onSaved && onSaved(r.data);
      // Ne pas fermer tout de suite — l'utilisateur peut vouloir envoyer aux fournisseurs
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la génération du devis');
    } finally {
      setSaving(false);
    }
  };

  // Charge la liste des fournisseurs quand on ouvre le modal
  const openFournisseursModal = async () => {
    setShowFournisseursModal(true);
    setLoadingFours(true);
    try {
      const r = await axios.get('/fournisseurs');
      setFournisseurs(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Impossible de charger les fournisseurs");
      setFournisseurs([]);
    } finally {
      setLoadingFours(false);
    }
  };

  // Lien public du PDF (inclus dans le message WhatsApp / WeChat)
  const [shareUrl, setShareUrl] = useState('');
  useEffect(() => {
    if (!showFournisseursModal || !savedDevis?.id) return;
    setShareUrl('');
    axios.post('/share-links', { kind: 'devis_conception', doc_id: savedDevis.id })
      .then((r) => setShareUrl(`${process.env.REACT_APP_BACKEND_URL}/api/public/share/${r.data.token}`))
      .catch(() => toast.error('Impossible de préparer le lien du PDF'));
  }, [showFournisseursModal, savedDevis]);

  const downloadDevisPdf = async () => {
    if (!savedDevis?.id) return;
    try {
      const r = await axios.get(`/conception/devis/${savedDevis.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Commande_${savedDevis.numero || savedDevis.id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch {
      toast.error("Impossible de télécharger le PDF");
    }
  };

  const normalizePhone = (phone = '') => phone.replace(/[^0-9]/g, '');

  const buildMessage = (fournisseur) => {
    const total = fmtXAF(savedDevis?.total_ttc || savedDevis?.total_general || 0);
    const items = (savedDevis?.supports_produits || []).map(it => `• ${it.designation} ×${it.quantite}`).join('\n');
    return `Bonjour ${fournisseur.nom || ''},\n\nMerci de nous préparer la commande ci-dessous :\n\n${items}\n\nMontant total : ${total} XAF\nRéf. ${savedDevis?.numero || ''}\n\nPDF détaillé : ${shareUrl}\n\nCordialement,\nSESAM`;
  };

  const sendViaWhatsapp = (fournisseur) => {
    const phone = normalizePhone(fournisseur.telephone);
    if (!phone) { toast.error("Ce fournisseur n'a pas de numéro WhatsApp"); return; }
    if (!shareUrl) { toast.info('Lien du PDF en préparation, réessayez dans un instant'); return; }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildMessage(fournisseur))}`, '_blank', 'noopener,noreferrer');
    toast.success(`WhatsApp ouvert pour ${fournisseur.nom}`);
  };

  const sendViaWechat = async (fournisseur) => {
    if (!shareUrl) { toast.info('Lien du PDF en préparation, réessayez dans un instant'); return; }
    try { await navigator.clipboard.writeText(buildMessage(fournisseur)); } catch { /* presse-papier indisponible */ }
    toast.success(`Message copié${fournisseur.telephone ? ` · Numéro ${fournisseur.telephone}` : ''} — collez-le dans WeChat`);
    window.location.href = 'weixin://';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1400px] w-[98vw] h-[92vh] p-0 overflow-hidden flex flex-col"
        data-testid="supports-imprimes-modal"
      >
        {/* Header sticky */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white sticky top-0 z-10">
          <div>
            <div className="text-xs text-slate-500 font-medium">Conception ▸ Devis</div>
            <h2 className="text-2xl font-bold text-slate-900">Nouveau devis — Supports imprimés</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="btn-back-supports-devis">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel-devis">
              {savedDevis ? 'Fermer' : 'Enregistrer brouillon'}
            </Button>
            {!savedDevis ? (
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={save}
                disabled={!canSave || saving}
                data-testid="btn-generate-devis"
              >
                <FileText className="h-4 w-4 mr-1.5" />
                {saving ? 'Génération…' : 'Générer le devis'}
              </Button>
            ) : (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={openFournisseursModal}
                data-testid="btn-envoyer-fournisseurs"
              >
                <Send className="h-4 w-4 mr-1.5" />
                Envoyer aux fournisseurs
              </Button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_400px]">
          {/* Left column — scrollable */}
          <div className="overflow-y-auto p-5 space-y-5 bg-slate-50">
            {/* Sections 1-2-3 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 1. Choisir l'entreprise */}
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="section-prospects">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="text-sm font-bold text-slate-800">1. Choisir l&apos;entreprise</div>
                  <QuickAddEntrepriseButton onCreated={(p) => { setProspects(prev => [p, ...prev]); setSelectedProspect(p); }} />
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    className="pl-8"
                    placeholder="Rechercher une entreprise…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="input-search-prospect"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                  {loadingProspects && <div className="text-xs text-slate-500 p-2">Chargement…</div>}
                  {!loadingProspects && filteredProspects.length === 0 && (
                    <div className="text-xs text-slate-500 p-2 italic">Aucun prospect trouvé</div>
                  )}
                  {filteredProspects.map((p) => {
                    const sel = selectedProspect?.id === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedProspect(p)}
                        className={`w-full text-left rounded-lg p-2.5 border transition-colors ${
                          sel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-800 border-slate-200 hover:bg-slate-50'
                        }`}
                        data-testid={`prospect-item-${p.id}`}
                      >
                        <div className="text-sm font-semibold truncate">{p.entreprise || 'Sans nom'}</div>
                        <div className={`text-xs truncate ${sel ? 'text-blue-100' : 'text-slate-500'}`}>{p.email || '—'}</div>
                        <div className={`text-xs ${sel ? 'text-blue-100' : 'text-slate-500'}`}>{p.telephone || '—'}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Informations de l'entreprise */}
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="section-infos">
                <div className="text-sm font-bold text-slate-800 mb-3">2. Informations de l&apos;entreprise</div>
                {selectedProspect ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Nom :</span><span className="font-semibold text-slate-800">{selectedProspect.entreprise || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Contact :</span><span className="text-slate-800">{selectedProspect.contact || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Email :</span><span className="text-blue-600 break-all">{selectedProspect.email || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Téléphone :</span><span className="text-slate-800">{selectedProspect.telephone || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Adresse :</span><span className="text-slate-800">{selectedProspect.adresse || '—'}</span></div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 text-slate-400 text-sm italic">
                    <Building2 className="h-8 w-8 mb-2" />
                    Sélectionnez une entreprise à gauche
                  </div>
                )}
              </div>

              {/* 3. Transport */}
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="section-transport">
                <div className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5"><Truck className="h-4 w-4" />3. Transport</div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Mode de transport</Label>
                    <Select value={transport.mode} onValueChange={(v) => setTransport({ ...transport, mode: v })}>
                      <SelectTrigger data-testid="input-transport-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TRANSPORT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Lieu de livraison</Label>
                    <Input
                      value={transport.lieu}
                      onChange={(e) => setTransport({ ...transport, lieu: e.target.value })}
                      placeholder="Ex. Douala"
                      data-testid="input-transport-lieu"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Prix du transport (XAF)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={transport.prix}
                      onChange={(e) => setTransport({ ...transport, prix: e.target.value })}
                      data-testid="input-transport-prix"
                    />
                  </div>
                  <div className="rounded-md bg-blue-50 border border-blue-100 text-blue-700 text-xs px-2.5 py-2">
                    ⓘ Vous pouvez saisir le prix manuellement.
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Supports de communication */}
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="section-supports">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-sm font-bold text-slate-800">4. Supports de communication</div>
                <div className="text-xs text-slate-500">
                  {selectedKeys.size > 0 ? `${selectedKeys.size} support(s) sélectionné(s)` : 'Cochez les supports désirés'}
                </div>
              </div>
              {/* Onglets de catégories */}
              <div className="flex gap-2 mb-3 flex-wrap">
                {SUPPORTS_CATEGORIES.map(cat => {
                  const count = items.filter(i => i.category === cat.key).length;
                  const sel = items.filter(i => i.category === cat.key && selectedKeys.has(i.key)).length;
                  return (
                    <button
                      key={cat.key}
                      onClick={() => setActiveCategoryTab(cat.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${activeCategoryTab === cat.key ? `${cat.bg} ${cat.color} ring-2 ring-current` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid={`cat-tab-${cat.key}`}
                    >
                      {cat.label} <span className="opacity-70">({sel}/{count})</span>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                {visibleItems.map((it) => {
                  const Icon = ICON_MAP[it.icon] || Package;
                  const isSelected = selectedKeys.has(it.key);
                  const toggleSelect = () => {
                    const next = new Set(selectedKeys);
                    if (next.has(it.key)) next.delete(it.key); else next.add(it.key);
                    setSelectedKeys(next);
                  };
                  return (
                    <div key={it.key} className={`border rounded-xl p-3 bg-white shadow-sm hover:shadow transition-shadow relative ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'}`} data-testid={`support-card-${it.key}`}>
                      {/* Case à cocher */}
                      <label className="absolute top-2 left-2 z-10 flex items-center gap-1 cursor-pointer" title="Inclure dans le devis">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={toggleSelect}
                          className="w-4 h-4 accent-blue-600 cursor-pointer"
                          data-testid={`chk-${it.key}`}
                        />
                      </label>
                      <div className={`h-28 rounded-lg mb-2 flex items-center justify-center bg-gradient-to-br ${it.color} ${isSelected ? '' : 'opacity-70'}`}>
                        <Icon className="h-14 w-14 text-slate-700/70" strokeWidth={1.4} />
                      </div>
                      <div className="text-center text-sm font-semibold text-slate-800 truncate" title={it.designation}>{it.designation}</div>
                      <div className={`mt-2 space-y-1.5 ${isSelected ? '' : 'opacity-50 pointer-events-none'}`}>
                        <div>
                          <Label className="text-[10px] text-slate-500">Quantité</Label>
                          <Input
                            type="number"
                            min={0}
                            value={it.qte}
                            onChange={(e) => updateItem(it.key, 'qte', e.target.value)}
                            className="h-8 text-sm text-right"
                            data-testid={`qte-${it.key}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500">Prix unitaire (XAF)</Label>
                          <Input
                            type="number"
                            min={0}
                            value={it.prix}
                            onChange={(e) => updateItem(it.key, 'prix', e.target.value)}
                            className="h-8 text-sm text-right"
                            data-testid={`prix-${it.key}`}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end mt-2">
                        <button className="text-red-500 hover:text-red-700" onClick={() => removeItem(it.key)} title="Retirer">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {/* Placeholder card "Ajouter un support" */}
                <button
                  onClick={addItem}
                  className="border-2 border-dashed border-slate-300 rounded-xl p-3 flex flex-col items-center justify-center text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors min-h-[220px]"
                  data-testid="btn-add-support-card"
                >
                  <Plus className="h-8 w-8 mb-1" />
                  <span className="text-xs font-medium">Ajouter<br/>un support</span>
                </button>
              </div>
            </div>
          </div>

          {/* Right column — Récapitulatif */}
          <div className="border-l border-slate-200 bg-white flex flex-col overflow-hidden">
            <div className="p-5 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-slate-900">Récapitulatif du devis</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="recap-panel">
              {/* Table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-slate-500 text-xs uppercase">
                      <th className="text-left px-3 py-2 font-semibold">Désignation</th>
                      <th className="text-right px-2 py-2 font-semibold w-14">Qté</th>
                      <th className="text-right px-2 py-2 font-semibold w-20">PU (XAF)</th>
                      <th className="text-right px-3 py-2 font-semibold w-24">Total (XAF)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filledItems.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-6 text-slate-400 italic text-xs">Ajoutez au moins un support</td></tr>
                    )}
                    {filledItems.map((it) => (
                      <tr key={it.key} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-800 truncate">{it.designation}</td>
                        <td className="px-2 py-2 text-right text-slate-800">{it.qte}</td>
                        <td className="px-2 py-2 text-right text-slate-800">{fmtXAF(it.prix)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900">{fmtXAF(Number(it.prix) * Number(it.qte))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totaux */}
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between border-t pt-3">
                  <span className="text-slate-600">Sous-total produits</span>
                  <span className="font-semibold text-slate-900" data-testid="recap-sous-total">{fmtXAF(sousTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Transport</span>
                  <span className="font-semibold text-slate-900" data-testid="recap-transport">{fmtXAF(transportPrix)}</span>
                </div>
                <TvaToggle tva={tva} setTva={setTva} base={totalGeneral} testid="sup-tva" />
                <div className="flex justify-between pt-3 border-t text-base">
                  <span className="font-bold text-slate-900">{tva.on ? 'Total TTC (XAF)' : 'Total général (XAF)'}</span>
                  <span className="font-bold text-blue-700 text-xl" data-testid="recap-total">{fmtXAF(totalGeneral + tvaMt)}</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <Textarea
                  rows={3}
                  placeholder="Ajoutez une note (optionnel)…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="input-notes"
                />
              </div>

              {/* Validité */}
              <div>
                <Label>Validité du devis</Label>
                <Input
                  type="date"
                  value={validiteDate}
                  onChange={(e) => setValiditeDate(e.target.value)}
                  data-testid="input-validite"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Banner succès visible après génération */}
        {savedDevis && (
          <div className="px-6 py-3 bg-emerald-50 border-t border-emerald-200 flex items-center justify-between gap-3" data-testid="banner-devis-saved">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Check className="h-5 w-5 text-emerald-700" />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-emerald-900">Devis {savedDevis.numero} enregistré</div>
                <div className="text-xs text-emerald-700">
                  Vous pouvez maintenant l'envoyer directement à un fournisseur sur WhatsApp ou WeChat.
                </div>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadDevisPdf}
              className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100 flex-shrink-0"
              data-testid="btn-download-saved-pdf"
            >
              <Download className="h-3.5 w-3.5" /> Télécharger PDF
            </Button>
          </div>
        )}
      </DialogContent>

      {/* Modal Envoyer aux fournisseurs */}
      <Dialog open={showFournisseursModal} onOpenChange={setShowFournisseursModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="fournisseurs-send-modal">
          <div className="px-6 py-4 border-b bg-white">
            <div className="text-xs text-slate-500 font-medium">Supports imprimés ▸ Envoi</div>
            <h2 className="text-xl font-bold text-slate-900">Envoyer aux fournisseurs</h2>
            <p className="text-sm text-slate-500 mt-1">Choisissez un fournisseur et un canal (WhatsApp ou WeChat). Le message contient le lien du PDF du devis.</p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {loadingFours ? (
              <div className="py-12 text-center text-sm text-slate-400">Chargement des fournisseurs…</div>
            ) : fournisseurs.length === 0 ? (
              <div className="py-12 text-center">
                <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <div className="text-sm font-semibold text-slate-700">Aucun fournisseur enregistré</div>
                <div className="text-xs text-slate-500 mt-1">Ajoutez d'abord un fournisseur depuis le module Fournisseurs.</div>
              </div>
            ) : (
              fournisseurs.map(f => (
                <div key={f.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition" data-testid={`fournisseur-row-${f.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 text-white font-bold flex items-center justify-center flex-shrink-0">
                    {(f.nom || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900 text-sm truncate">{f.nom}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {f.specialite || f.categorie || '—'}
                      {f.telephone ? ` · ${f.telephone}` : ''}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => sendViaWhatsapp(f)}
                    disabled={!f.telephone}
                    className="h-9 px-3 gap-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                    data-testid={`btn-whatsapp-${f.id}`}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => sendViaWechat(f)}
                    disabled={!f.telephone}
                    variant="outline"
                    className="h-9 px-3 gap-1 border-slate-300 text-slate-700 font-semibold"
                    data-testid={`btn-wechat-${f.id}`}
                  >
                    <MessageCircle className="h-3.5 w-3.5 text-[#07C160]" />
                    WeChat
                  </Button>
                </div>
              ))
            )}
          </div>
          <div className="px-6 py-3 border-t bg-slate-50 flex justify-between items-center">
            <span className="text-xs text-slate-500">💡 Astuce : après l'ouverture de WhatsApp, cliquez sur l'icône trombone pour joindre le PDF téléchargé.</span>
            <Button variant="outline" onClick={() => setShowFournisseursModal(false)}>Fermer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
